import { useRef } from 'react';
import { Box, Container, Typography } from '@mui/material';
import { landingColors, landingFont, eyebrowSx, sectionHeadingSx } from '../landingTheme';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { getLandingIcon } from '../landingIcons';
import { WHY_BENEFITS, TESTIMONIAL } from '../landingContent';

export default function WhyChooseSection() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <Box
      ref={sectionRef}
      component="section"
      aria-label="Why merchQRy"
      sx={{ py: { xs: 8, md: 14 }, backgroundColor: landingColors.paper }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 5, md: 8 } }}>
          <Typography data-reveal sx={{ ...eyebrowSx, color: landingColors.indigo, fontFamily: landingFont, mb: 1.5 }}>
            Why merchQRy
          </Typography>
          <Typography data-reveal data-reveal-delay="0.06" component="h2" sx={{ ...sectionHeadingSx, fontFamily: landingFont, color: landingColors.ink, mb: 2 }}>
            Built for modern restaurants
          </Typography>
          <Typography data-reveal data-reveal-delay="0.12" sx={{ fontFamily: landingFont, color: landingColors.inkMuted, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.6 }}>
            Serve better, faster, and smarter, without rebuilding how your restaurant runs.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: { xs: 2, md: 3 }, mb: { xs: 5, md: 8 } }}>
          {WHY_BENEFITS.map((benefit, index) => {
            const Icon = getLandingIcon(benefit.icon);
            return (
              <Box
                key={benefit.title}
                data-reveal
                data-reveal-delay={((index % 3) * 0.08).toString()}
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: { xs: 2.5, md: 3 },
                  borderRadius: '18px',
                  transition: 'background-color 0.3s ease',
                  '&:hover': { backgroundColor: landingColors.paperAlt },
                }}
              >
                <Box
                  sx={{
                    width: 50,
                    height: 50,
                    minWidth: 50,
                    borderRadius: '12px',
                    backgroundColor: landingColors.indigoSoft,
                    color: landingColors.indigo,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon sx={{ fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography component="h3" sx={{ fontFamily: landingFont, fontWeight: 700, fontSize: '1.05rem', color: landingColors.ink, mb: 0.5 }}>
                    {benefit.title}
                  </Typography>
                  <Typography sx={{ fontFamily: landingFont, color: landingColors.inkMuted, fontSize: '0.9rem', lineHeight: 1.6 }}>
                    {benefit.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box
          data-reveal
          sx={{
            maxWidth: 860,
            mx: 'auto',
            textAlign: 'center',
            p: { xs: 3, md: 5 },
            borderRadius: '24px',
            backgroundColor: landingColors.paperAlt,
            border: `1px solid ${landingColors.line}`,
          }}
        >
          <Typography sx={{ fontFamily: landingFont, color: landingColors.ink, fontSize: { xs: '1.1rem', md: '1.4rem' }, lineHeight: 1.6, fontWeight: 600, mb: 2 }}>
            “{TESTIMONIAL.quote}”
          </Typography>
          <Typography sx={{ fontFamily: landingFont, color: landingColors.indigo, fontWeight: 700, fontSize: '0.95rem' }}>
            {TESTIMONIAL.author}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
